import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import InfiniteScroll from "react-infinite-scroll-component";
import Spinner from "../components/UI/Spinner/Spinner";

// Components
import { PageHeader } from "../components/PageSpecificComponents";
import { Button } from "../components/UI";
import ConfirmDialog from "../components/UI/ConfirmDialog/ConfirmDialog";

// Page specific components
import BranchCard from "../components/PageSpecificComponents/pagesComponents/manageBranches/branchCard/BranchCard";

// Builders
import * as buttonDesignType from "../builders/buttonDesign.types";

// Actions
import { deleteBranch } from "../redux/actions/app.actions";

// Calls
import {
  fetchBranchesRequest,
  removeBranchRequest
} from "../api/calls/branch.call";

// Styles
import { ManageBranchesStyles } from "../pagesStyles/manageBranches.styles";
import { SearchIcon } from "../SVGS";
import useDebounce from "../hooks/useDebounce";

const ManageBranches = () => {
  const [branches, setBranches] = useState([]);
  const [branchesPagination, setBranchesPagination] = useState({});
  const { setSearchQuery, debouncedSearchQuery } = useDebounce();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const [branchToDelete, setBranchToDelete] = useState(null);
  const lastBranch = branches[branches.length - 1];

  const getBranches = async (lastBranchId, searchQuery = "") => {
    if (searchQuery && !lastBranchId) {
      setLoading(true);
    }
    const response = await fetchBranchesRequest(lastBranchId, searchQuery);
    setLoading(false);
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }

    if (lastBranchId) {
      setBranches(prev => [
        ...prev,
        ...(Array.isArray(response.branches)
          ? response.branches
          : [response.branches])
      ]);
    } else {
      setBranches(
        Array.isArray(response.branches)
          ? response.branches
          : [response.branches]
      );
    }

    setBranchesPagination(response.pagination);
  };

  useEffect(() => {
    setBranches([]);
    setBranchesPagination({});
    getBranches(undefined, debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  const confirmDeleteBranch = async () => {
    const id = branchToDelete;
    setBranchToDelete(null);
    const response = await removeBranchRequest(id);
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }
    setBranches(pervValues => pervValues.filter(branch => branch.id !== id));
    dispatch(deleteBranch(id));
    toast.success(response.message);
  };

  const override = {
    display: "block",
    borderColor: "#d2e6f2",
    boxSizing: "content-box"
  };

  return (
    <ManageBranchesStyles>
      <PageHeader title="Manage Branches">
        <div className="searchContainer">
          <SearchIcon className="icon" />
          <input
            className="searchInput"
            placeholder="Search by name"
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <Button
          onClick={() => history.push("/create-branch")}
          buttonDesignType={buttonDesignType.PRIMARY}
        >
          + Add New Branch
        </Button>
      </PageHeader>

      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "20px"
          }}
        >
          <Spinner size={22} color="#292359" cssOverride={override} />
        </div>
      ) : (
        <InfiniteScroll
          dataLength={branches.length}
          next={() => getBranches(lastBranch?.id, debouncedSearchQuery)}
          hasMore={branchesPagination.has_more}
        >
          <div className="branchesList">
            {branches?.map(branch => {
              const branchId = branch.id;
              return (
                <BranchCard
                  {...branch}
                  key={branchId}
                  deleteBranch={() => setBranchToDelete(branchId)}
                />
              );
            })}
          </div>
        </InfiniteScroll>
      )}
      <ConfirmDialog
        isOpen={!!branchToDelete}
        onClose={() => setBranchToDelete(null)}
        onConfirm={confirmDeleteBranch}
        title="Delete Branch?"
        message="Are you sure you want to delete this branch? This action cannot be undone."
      />
    </ManageBranchesStyles>
  );
};

export default ManageBranches;
